import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Typography, Form, Input, Button, message, Row, Col, Tag, Divider, Avatar, Modal, Switch, Space } from 'antd';
import { ToolOutlined, UserOutlined, LockOutlined, MailOutlined, IdcardOutlined, SaveOutlined, KeyOutlined, DownloadOutlined, MobileOutlined, SettingOutlined, DatabaseOutlined, RiseOutlined, CalendarOutlined, VideoCameraOutlined, HistoryOutlined, FundOutlined, DashboardOutlined, ArrowLeftOutlined, CloudServerOutlined, TeamOutlined, PlayCircleOutlined, WalletOutlined, FileTextOutlined, CloudSyncOutlined } from '@ant-design/icons';
import api from '../utils/api';
import BackupStatus from './BackupStatus';
import StaffPerformance from './StaffPerformance';
import ArtistDashboard from './ArtistDashboard';
import ReleaseCalendar from './ReleaseCalendar';
import ChannelActivity from './ChannelActivity';
import Royalties from './Royalties';
import ActivityLog from './ActivityLog';
import MonthlyReport from './MonthlyReport';
import ServerMonitor from './ServerMonitor';
import GoogleSheetSync from './GoogleSheetSync';

const { Title, Text } = Typography;

function ProfileTab({ user, onUserUpdate }) {
  const [profileForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') setInstallPrompt(null);
    } else {
      setShowInstallHelp(true);
    }
  };

  const saveProfile = async (vals) => {
    setSavingProfile(true);
    try {
      const res = await api.put('/auth/profile', { full_name: vals.full_name, email: vals.email });
      const updated = res.data.user;
      const merged = { ...user, full_name: updated.full_name, email: updated.email };
      localStorage.setItem('user', JSON.stringify(merged));
      if (onUserUpdate) onUserUpdate(merged);
      message.success('Profile updated');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (vals) => {
    if (vals.new_password !== vals.confirm_password) {
      message.error('New password and confirm password do not match');
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', { current_password: vals.current_password, new_password: vals.new_password });
      message.success('Password changed');
      pwdForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card bordered={false} style={{ height: '100%' }} title={<span><IdcardOutlined /> Profile</span>}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <Avatar size={72} style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }} icon={<UserOutlined />} />
              <div style={{ marginTop: 10 }}><Text strong style={{ fontSize: 16 }}>{user.full_name}</Text></div>
              <div>
                <Tag color="purple">@{user.username}</Tag>
                <Tag color="blue">{user.role}</Tag>
                {user.staff_id && <Tag color="geekblue">ID: {user.staff_id}</Tag>}
              </div>
            </div>
            <Form form={profileForm} layout="vertical" initialValues={{ full_name: user.full_name, email: user.email }} onFinish={saveProfile}>
              <Form.Item name="full_name" label="Full Name" rules={[{ required: true, message: 'Please enter your full name' }]}>
                <Input prefix={<UserOutlined />} placeholder="Full name" />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
                <Input prefix={<MailOutlined />} placeholder="email@example.com" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingProfile}>Save Profile</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} style={{ height: '100%' }} title={<span><KeyOutlined /> Change Password</span>}>
            <Form form={pwdForm} layout="vertical" onFinish={changePassword}>
              <Form.Item name="current_password" label="Current Password" rules={[{ required: true, message: 'Please enter your current password' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Current password" />
              </Form.Item>
              <Form.Item name="new_password" label="New Password" rules={[{ required: true, message: 'Please enter a new password' }, { min: 6, message: 'At least 6 characters' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="New password" />
              </Form.Item>
              <Form.Item name="confirm_password" label="Confirm New Password" dependencies={['new_password']}
                rules={[{ required: true, message: 'Please confirm your password' }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('new_password') === value) return Promise.resolve(); return Promise.reject(new Error('Passwords do not match')); } })]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<KeyOutlined />} loading={savingPwd}>Change Password</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Divider />
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card bordered={false} title={<span><MobileOutlined /> Install App</span>}>
            <Text>Install Bainsla Music Dashboard as an app on your phone or computer.</Text>
            <div style={{ marginTop: 16 }}>
              <Button type="primary" icon={<DownloadOutlined />} size="large" onClick={handleInstall}
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: 10, height: 44, fontWeight: 600 }}>
                Install App
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Modal title="Install Bainsla Music App" open={showInstallHelp} onCancel={() => setShowInstallHelp(false)} footer={null} width={480}>
        <div style={{ lineHeight: 2 }}>
          <p><b>Android (Chrome):</b></p>
          <ol>
            <li>Tap <b>:</b> (3 dots) in Chrome top-right</li>
            <li>Tap <b>"Install app"</b> or <b>"Add to Home Screen"</b></li>
            <li>Tap <b>"Install"</b></li>
          </ol>
          <p><b>iPhone (Safari):</b></p>
          <ol>
            <li>Tap the <b>Share</b> button (square with arrow)</li>
            <li>Scroll down and tap <b>"Add to Home Screen"</b></li>
            <li>Tap <b>"Add"</b></li>
          </ol>
          <p><b>Desktop (Chrome):</b></p>
          <ol>
            <li>Click the <b>install icon</b> in the address bar</li>
            <li>Click <b>"Install"</b></li>
          </ol>
        </div>
      </Modal>
    </div>
  );
}

function MaintenanceCard() {
  const [on, setOn] = useState(false);
  const [text, setText] = useState('');
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/maintenance')
      .then(r => {
        setOn(!!r.data?.on);
        setText(r.data?.message || '');
        setUntil(r.data?.until || '');
      })
      .catch(() => {});
  }, []);

  const save = async (nextOn, nextText = text, nextUntil = until) => {
    setSaving(true);
    try {
      const res = await api.post('/settings/maintenance', { on: nextOn, message: nextText, until: nextUntil });
      setOn(!!res.data.on);
      message.success(res.data.on ? 'Maintenance Mode ON — sabko wait screen dikhega' : 'Maintenance Mode OFF — dashboard normal');
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not save maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ borderRadius: 12, borderTop: '3px solid #fa8c16' }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <ToolOutlined style={{ fontSize: 22, color: '#fa8c16' }} />
        <Text strong style={{ fontSize: 16 }}>Maintenance Mode</Text>
        <Switch checked={on} loading={saving} onChange={(v) => save(v)} checkedChildren="ON" unCheckedChildren="OFF" />
        {on && <Tag color="orange">Staff & clients ko sirf wait screen dikh raha hai</Tag>}
      </Space>
      <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
        Tick karte hi sabhi staff aur client ka dashboard band ho jayega aur unhe "Maintenance chal raha hai, please wait"
        dikhega. Aap (admin) dashboard normal use kar sakte ho.
      </div>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={14}>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Message (optional) — jaise: Dashboard update ho raha hai, 1 ghante me wapas"
            maxLength={500}
          />
        </Col>
        <Col xs={24} md={6}>
          <Input value={until} onChange={(e) => setUntil(e.target.value)} placeholder="Expected time (optional)" maxLength={100} />
        </Col>
        <Col xs={24} md={4}>
          <Button block loading={saving} onClick={() => save(on)}>Save Message</Button>
        </Col>
      </Row>
    </Card>
  );
}

const adminSections = [
  { key: 'backup', label: 'Backup Status', icon: <DatabaseOutlined />, color: '#52c41a', desc: 'Database backup, server disk space' },
  { key: 'server-monitor', label: 'Server Monitor', icon: <CloudServerOutlined />, color: '#fa541c', desc: 'Live CPU, RAM, Disk, Network stats' },
  { key: 'performance', label: 'Staff Performance', icon: <RiseOutlined />, color: '#1890ff', desc: 'Staff ratings and reviews' },
  { key: 'artists', label: 'Artist Dashboard', icon: <TeamOutlined />, color: '#722ed1', desc: 'Artists overview and stats' },
  { key: 'calendar', label: 'Release Calendar', icon: <CalendarOutlined />, color: '#eb2f96', desc: 'Upcoming song releases' },
  { key: 'channel', label: 'Channel Activity', icon: <PlayCircleOutlined />, color: '#13c2c2', desc: 'YouTube channel activity log' },
  { key: 'royalties', label: 'Royalty / Payouts', icon: <WalletOutlined />, color: '#faad14', desc: 'Royalty tracking and payouts' },
  { key: 'activity-log', label: 'Activity Log', icon: <HistoryOutlined />, color: '#595959', desc: 'System-wide activity history' },
  { key: 'monthly-report', label: 'Monthly Report', icon: <FileTextOutlined />, color: '#389e0d', desc: 'Monthly summary reports' },
  { key: 'gsheet', label: 'Google Sheet Sync', icon: <CloudSyncOutlined />, color: '#0f9d58', desc: 'Two-way sync with Google Sheet' },
];

const sectionComponents = {
  'backup': () => <BackupStatus />,
  'server-monitor': () => <ServerMonitor />,
  'performance': () => <StaffPerformance />,
  'artists': () => <ArtistDashboard />,
  'calendar': (user) => <ReleaseCalendar user={user} />,
  'channel': (user) => <ChannelActivity user={user} />,
  'royalties': () => <Royalties />,
  'activity-log': () => <ActivityLog />,
  'monthly-report': () => <MonthlyReport />,
  'gsheet': () => <GoogleSheetSync />,
};

export default function Settings({ user, onUserUpdate }) {
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const location = useLocation();
  const urlSection = new URLSearchParams(location.search).get('section');
  const [activeSection, setActiveSection] = useState(null);
  useEffect(() => {
    if (urlSection && sectionComponents[urlSection]) setActiveSection(urlSection);
  }, [urlSection, location.key]);

  if (activeSection) {
    const section = adminSections.find(s => s.key === activeSection);
    const renderFn = sectionComponents[activeSection];
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => setActiveSection(null)}
            style={{ fontSize: 16, padding: 0, fontWeight: 600, color: '#667eea' }}>
            Back to Settings
          </Button>
          <Title level={3} style={{ margin: '8px 0 0 0' }}>
            <span style={{ color: section?.color, marginRight: 8 }}>{section?.icon}</span>
            {section?.label}
          </Title>
        </div>
        {renderFn ? renderFn(user) : null}
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ margin: 0 }}><SettingOutlined /> Settings</Title>
      <Text type="secondary">Profile, backup, performance, server monitoring, and more</Text>

      <div style={{ marginTop: 16 }}>
        <ProfileTab user={user} onUserUpdate={onUserUpdate} />
      </div>

      {isAdmin && (
        <>
          <Divider style={{ margin: '24px 0 16px' }}>
            <Text strong style={{ fontSize: 16, color: '#667eea' }}>Admin Tools</Text>
          </Divider>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <div style={{ marginBottom: 16 }}><MaintenanceCard /></div>
          )}
          <Row gutter={[16, 16]}>
            {adminSections.map(s => (
              <Col xs={12} sm={8} md={6} key={s.key}>
                <Card
                  hoverable
                  onClick={() => setActiveSection(s.key)}
                  style={{
                    borderRadius: 12,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderTop: `3px solid ${s.color}`,
                    height: '100%',
                    transition: 'all 0.3s',
                  }}
                  bodyStyle={{ padding: '20px 12px' }}
                >
                  <div style={{ fontSize: 32, color: s.color, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: '#999', lineHeight: 1.3 }}>{s.desc}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
}
